import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatNumber, formatUsd, messages, type Quota } from '@nesc/shared';
import { api, type ListResponse, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/feedback';
import { FilterChips, PageHeader } from '@/components/common/page';
import { cn } from '@/lib/utils';

type CostRow = { key: string; label: string; callCount: number; unitCount: number; costUsd: number };

/** S81 コスト */
export const UsagePage = () => {
  const [groupBy, setGroupBy] = useState<'provider' | 'feature' | 'user' | 'campaign' | 'date'>('provider');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const query = { groupBy, from: from || undefined, to: to || undefined };

  const { data, isLoading } = useQuery({
    queryKey: qk.costs(query),
    queryFn: () => api.get<ListResponse<CostRow>>('/usage/costs', query),
  });

  const { data: quota } = useQuery({
    queryKey: qk.quota,
    queryFn: () => api.get<SingleResponse<Quota>>('/usage/quota'),
  });

  return (
    <>
      <PageHeader back="/settings" title={messages.usage.title} />

      {quota && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <QuotaBar
            label={messages.quota.aiCost}
            used={quota.data.aiCost.usedUsd}
            limit={quota.data.aiCost.limitUsd}
            level={quota.data.aiCost.level}
            format={(v) => formatUsd(v)}
          />
          <QuotaBar
            label={messages.quota.searchCalls}
            used={quota.data.search.usedCalls}
            limit={quota.data.search.limitCalls}
            level={quota.data.search.level}
            format={formatNumber}
          />
          <QuotaBar
            label={messages.quota.dailySend}
            used={quota.data.send.usedToday}
            limit={quota.data.send.dailyLimit}
            level={quota.data.send.level}
            format={formatNumber}
          />
        </div>
      )}

      <FilterChips className="mb-3">
        <Select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
          aria-label="集計の軸"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        >
          <option value="provider">{messages.usage.provider}別</option>
          <option value="feature">{messages.usage.feature}別</option>
          <option value="user">ユーザー別</option>
          <option value="campaign">キャンペーン別</option>
          <option value="date">日別</option>
        </Select>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="開始日"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="終了日"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        />
      </FilterChips>

      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : !data || data.data.length === 0 ? (
        <EmptyState title="対象の期間に利用はありません" />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              合計 {formatUsd((data.meta.totalCostUsd as number) ?? 0)}（
              {formatNumber((data.meta.totalCalls as number) ?? 0)} 回）
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-cell">
              <thead>
                <tr className="border-b border-border-strong text-note text-foreground-muted">
                  <th scope="col" className="py-2 text-left">対象</th>
                  <th scope="col" className="py-2 text-right">{messages.usage.calls}</th>
                  <th scope="col" className="py-2 text-right">{messages.usage.tokens}</th>
                  <th scope="col" className="py-2 text-right">{messages.usage.cost}</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => (
                  <tr key={row.key} className="border-b border-border last:border-0">
                    <td className="py-2">{row.label}</td>
                    <td className="tabular py-2 text-right">{formatNumber(row.callCount)}</td>
                    <td className="tabular py-2 text-right">{formatNumber(row.unitCount)}</td>
                    <td className="tabular py-2 text-right">{formatUsd(row.costUsd, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

const QuotaBar = ({
  label,
  used,
  limit,
  level,
  format,
}: {
  label: string;
  used: number;
  limit: number | null;
  level: string;
  format: (value: number) => string;
}) => {
  const percent = limit && limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 0;
  return (
    <Card>
      <CardContent className="space-y-1.5 pt-4">
        <p className="text-note text-foreground-muted">{label}</p>
        <p className="tabular text-h3">
          {format(used)}
          <span className="ml-1 text-body font-normal text-foreground-muted">
            / {limit ? format(limit) : '—'}
          </span>
        </p>
        <div
          className={cn(
            'h-2 w-full overflow-hidden rounded-control bg-muted',
            level !== 'ok' && 'ring-2 ring-foreground',
          )}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} の使用率`}
        >
          <div className="h-full bg-foreground" style={{ width: `${percent}%` }} />
        </div>
      </CardContent>
    </Card>
  );
};
